import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import StyledModal from 'src/components/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'react-bootstrap/Button';
import StyledAlert from 'src/components/Alert';
import { Form, Formik, Field } from 'formik';
import { SpaceService } from 'src/services/SpaceService';
import { IMainCommonProps } from 'src/types';
import StyledInput from 'src/components/Controls/Input';
import { useTranslation } from 'react-i18next';
import { validate } from '@common/utils/form-validation';

interface IProps {
  handleClose: (selectedLocation?) => void;
  show: boolean;
  lang: string;
  name?: string;
}

const AddSpaceModal: React.FunctionComponent<IProps & IMainCommonProps> = ({
  show,
  handleClose,
  handleRequest,
  lang,
  name,
}) => {
  const { t } = useTranslation();
  const [domainAlreadyUsed, setDomainAlreadyUsed] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const addSpace = values => {
    setIsSaving(true);
    handleRequest(SpaceService.store(values))
      .then(res => {
        window.location.href = `https://${res.body.data.custom_domain}/${lang}/admin`;
        handleClose();
      })
      .catch(response => {
        if (response.body.errors?.custom_domain?.length) {
          setDomainAlreadyUsed(true);
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <StyledModal
      show={show}
      handleClose={handleClose}
      title={t<string>('pages.panel.add-space-modal.create-org')}
      titleIcon={<FontAwesomeIcon icon={['fas', 'building']} />}
    >
      <Formik
        initialValues={{
          name: name ?? '',
          custom_domain: '',
        }}
        onSubmit={addSpace}
      >
        <>
          <Modal.Body>
            <StyledAlert
              variant="info"
              show={true}
              title={t<string>('pages.panel.add-space-modal.introduction-text')}
              text={t<string>('pages.panel.add-space-modal.introduction-sub-text')}
            />

            <StyledAlert
              variant="danger"
              show={domainAlreadyUsed}
              title={t<string>(
                'pages.panel.add-space-modal.domain-already-used-alert.title'
              )}
              text={t<string>(
                'pages.panel.add-space-modal.domain-already-used-alert.text'
              )}
            />

            <Form>
              <Field name="name" validate={validate(['required'])}>
                {({ field, form, meta }) => (
                  <StyledInput
                    field={field}
                    form={form}
                    meta={meta}
                    placeholder={t<string>(
                      'pages.panel.add-space-modal.enter-org-name-short'
                    )}
                  />
                )}
              </Field>
              <Field
                name="custom_domain"
                validate={validate(['required', 'domain', 'should-not-start-with-www'])}
              >
                {({ field, form, meta }) => (
                  <StyledInput
                    field={field}
                    form={form}
                    meta={meta}
                    placeholder={t<string>(
                      'pages.panel.add-space-modal.domain-placeholder'
                    )}
                    onChange={value => {
                      form.setFieldValue(field.name, value);
                      setDomainAlreadyUsed(false);
                    }}
                  />
                )}
              </Field>

              <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                  {t<string>('common.cancel')}
                </Button>
                <Button variant="primary" type="submit" disabled={isSaving}>
                  {isSaving ? (
                    <FontAwesomeIcon spin icon={['far', 'spinner']} />
                  ) : (
                    <FontAwesomeIcon icon={['far', 'check']} />
                  )}
                  &nbsp;{t<string>('pages.panel.add-space-modal.create')}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal.Body>
        </>
      </Formik>
    </StyledModal>
  );
};

export default AddSpaceModal;